import React, { useCallback, useMemo, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { apiUrl } from "../../config/api";
import ConfirmDialog from "../globalcontrols/confirmdialog/ConfirmDialog";
import "./tableeCarousel.css";

const emptyLanding = {
  id: "",
  slug: "",
  title: "",
  subtitle: "",
  ctaLabel: "Ver catalogo",
  ctaPath: "/catalogo",
  secondaryCtaLabel: "Cotizar con Tablee",
  secondaryCtaPath: "mailto:contacto@tablee.cl",
  blocks: [],
  marketingBindings: [],
  isActive: true,
};

const GOOGLE_VALUE_TRACK_SUFFIX = [
  "utm_source=google",
  "utm_medium=cpc",
  "utm_campaign=tablas_de_quesos",
  "utm_id={campaignid}",
  "utm_content={creative}",
  "utm_term={keyword}",
  "adgroup_id={adgroupid}",
  "device={device}",
  "network={network}",
  "matchtype={matchtype}",
].join("&");

function createMarketingBinding(provider = "google_ads") {
  return {
    provider,
    providerLabel: provider === "google_ads" ? "Google Ads" : provider === "meta_ads" ? "Meta Ads" : "Manual",
    campaignExternalId: "",
    campaignName: "",
    campaignStatus: "unknown",
    intentExternalId: "",
    intentKind: provider === "google_ads" ? "ad_group" : "manual",
    intentName: "",
    intentTerms: [],
    destinationStatus: "draft",
    trackingTemplate: "",
    finalUrlSuffix: provider === "google_ads" ? GOOGLE_VALUE_TRACK_SUFFIX : "",
    urlParameters: {},
    notes: "",
    isPrimary: true,
  };
}

function createGoogleTablesPilot(storefrontName = "Tablee", defaults = emptyLanding, campaignExternalId = "24030847091") {
  return {
    ...defaults,
    slug: "tablas-de-quesos-santiago",
    title: "Tablas de quesos para compartir en Santiago",
    subtitle: "Encuentra tablas listas para reuniones, celebraciones y regalos. Elige según la cantidad de personas y compra con despacho coordinado.",
    isActive: false,
    blocks: [
      {
        ...createBlock("hero"),
        id: "hero-tablas-google",
        eyebrow: `${storefrontName} · Tablas`,
        title: "Tablas de quesos para compartir en Santiago",
        body: "Encuentra tablas listas para reuniones, celebraciones y regalos. Elige según la cantidad de personas y compra con despacho coordinado.",
        primaryLabel: "Ver tablas disponibles",
        primaryPath: "#productos",
        secondaryLabel: "Ver despacho y condiciones",
        secondaryPath: "/despachos-y-retiros",
      },
      {
        ...createBlock("product_grid"),
        id: "productos-tablas-google",
        eyebrow: "Elige según tu ocasión",
        title: "Tablas para distintos tamaños de grupo",
        body: `Productos, precios y disponibilidad se actualizan directamente desde el catálogo de ${storefrontName}.`,
        categories: ["Tablas"],
        inStockOnly: true,
        showDescription: true,
      },
      {
        ...createBlock("text"),
        id: "confianza-tablas-google",
        eyebrow: "Compra informada",
        title: "Despacho coordinado y condiciones claras",
        body: "Revisa la cobertura, los tiempos de preparación y las condiciones de entrega antes de finalizar tu compra.",
        align: "center",
      },
    ],
    marketingBindings: [{
      ...createMarketingBinding("google_ads"),
      campaignExternalId,
      campaignName: `${storefrontName} Google Ads`,
      intentKind: "keyword",
      intentName: "Tabla de quesos",
      intentTerms: ["tabla de quesos", "tablas de quesos", "tabla para compartir"],
      notes: "Borrador piloto para alinear la intención de búsqueda con una selección viva de la categoría Tablas.",
    }],
  };
}

const blockTypeOptions = [
  { value: "hero", label: "Hero" },
  { value: "text", label: "Texto" },
  { value: "image", label: "Imagen" },
  { value: "button", label: "Boton" },
  { value: "product_grid", label: "Productos" },
  { value: "links", label: "Links" },
];

const tableePublicOrigin = ["localhost", "127.0.0.1"].includes(window.location.hostname)
  ? (process.env.REACT_APP_TABLEE_LOCAL_ORIGIN || "http://localhost:5180")
  : "https://www.tablee.cl";

function getPublicLandingUrl(landing = {}, publicBaseUrl = tableePublicOrigin) {
  const slug = normalizeSlug(landing.slug);
  const path = Array.isArray(landing.blocks) && landing.blocks.length
    ? `/campanas/${slug}/`
    : `/${slug}/`;
  return `${publicBaseUrl.replace(/\/+$/, "")}${path}`;
}

function createBlock(type, storefrontName = "Tablee") {
  const id = `block-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  if (type === "hero") return { id, type, eyebrow: storefrontName, title: "", body: "", imageUrl: "", primaryLabel: "Ver productos", primaryPath: "#productos", secondaryLabel: "", secondaryPath: "", align: "left" };
  if (type === "image") return { id, type, imageUrl: "", alt: "", caption: "", path: "", align: "center" };
  if (type === "button") return { id, type, label: "Comprar ahora", path: "/catalogo", style: "primary", align: "center" };
  if (type === "product_grid") return { id, type, eyebrow: "Catalogo", title: "Productos seleccionados", body: "", selectionMode: "filter", categories: [], productIds: [], inStockOnly: false, sort: "featured", limit: 12, showCategory: true, showDescription: false, showAddToCart: true, align: "left" };
  if (type === "links") return { id, type, title: "Tambien puedes visitar", links: [], align: "left" };
  return { id, type: "text", eyebrow: "", title: "", body: "", align: "left" };
}

function getSessionHeaders() {
  const session = JSON.parse(localStorage.getItem("sessionData") || "{}");
  return {
    Authorization: session.sessionKey || "",
    Username: session.username || "",
    "Content-Type": "application/json",
  };
}

function normalizeSlug(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/^\/+|\/+$/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function TableeLandings({ apiPrefix = "/tablee", storefrontName = "Tablee", publicBaseUrl, storeLogo = "/images/tablee_logo.png" } = {}) {
  const defaultLanding = useMemo(() => ({ ...emptyLanding, ...(apiPrefix !== "/tablee" ? {
    secondaryCtaLabel: `Consultar con ${storefrontName}`, secondaryCtaPath: "mailto:contacto@olivaymiel.cl",
  } : {}) }), [apiPrefix, storefrontName]);
  const sectionTypeDropdownRef = useRef(null);
  const categoryPickerRef = useRef(null);
  const categoryPickerMenuRef = useRef(null);
  const [landings, setLandings] = useState([]);
  const [form, setForm] = useState(defaultLanding);
  const [showModal, setShowModal] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [message, setMessage] = useState("");
  const [uploadingBlockId, setUploadingBlockId] = useState("");
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [newBlockType, setNewBlockType] = useState("hero");
  const [isNewBlockTypeOpen, setIsNewBlockTypeOpen] = useState(false);
  const [isCategoryPickerOpen, setIsCategoryPickerOpen] = useState(false);
  const [categorySearch, setCategorySearch] = useState("");
  const [categoryPickerPosition, setCategoryPickerPosition] = useState(null);
  const [editingBlock, setEditingBlock] = useState(null);
  const [editingBlockIndex, setEditingBlockIndex] = useState(-1);
  const [googleStructure, setGoogleStructure] = useState({
    status: "idle",
    campaigns: [],
    adGroups: [],
    keywords: [],
    message: "",
  });

  const loadLandings = useCallback(async () => {
    try {
      const response = await fetch(apiUrl(`${apiPrefix}/landings`), { headers: getSessionHeaders() });
      const data = await response.json();
      if (data.success) setLandings(data.landings || []);
      else setMessage(data.message || "No se pudieron cargar las landings.");
    } catch {
      setMessage("No se pudo conectar con la API.");
    }
  }, [apiPrefix]);

  const loadCatalogOptions = useCallback(async () => {
    try {
      const [productsResponse, categoriesResponse] = await Promise.all([
        fetch(apiUrl(`${apiPrefix}/products`), { headers: getSessionHeaders() }),
        fetch(apiUrl(`${apiPrefix}/categories`), { headers: getSessionHeaders() }),
      ]);
      const [productsData, categoriesData] = await Promise.all([productsResponse.json(), categoriesResponse.json()]);
      if (productsData.success) setProducts(productsData.products || []);
      if (categoriesData.success) {
        setCategories([...new Set((categoriesData.categories || []).map((item) => item.category).filter(Boolean))]);
      }
    } catch {
      setMessage("No se pudieron cargar las opciones del catalogo.");
    }
  }, [apiPrefix]);

  useEffect(() => {
    loadLandings();
    loadCatalogOptions();
  }, [loadLandings, loadCatalogOptions]);

  useEffect(() => {
    if (!isNewBlockTypeOpen) return undefined;
    function closeSectionTypeDropdown(event) {
      if (!sectionTypeDropdownRef.current?.contains(event.target)) setIsNewBlockTypeOpen(false);
    }
    document.addEventListener("pointerdown", closeSectionTypeDropdown);
    return () => document.removeEventListener("pointerdown", closeSectionTypeDropdown);
  }, [isNewBlockTypeOpen]);

  useEffect(() => {
    if (!isCategoryPickerOpen) return undefined;
    function positionCategoryPicker() {
      const bounds = categoryPickerRef.current?.getBoundingClientRect();
      if (!bounds) return;
      setCategoryPickerPosition({ top: bounds.bottom + 6, left: bounds.left, width: bounds.width });
    }
    function closeCategoryPicker(event) {
      if (!categoryPickerRef.current?.contains(event.target) && !categoryPickerMenuRef.current?.contains(event.target)) {
        setIsCategoryPickerOpen(false);
      }
    }
    positionCategoryPicker();
    document.addEventListener("click", closeCategoryPicker, true);
    window.addEventListener("resize", positionCategoryPicker);
    window.addEventListener("scroll", positionCategoryPicker, true);
    return () => {
      document.removeEventListener("click", closeCategoryPicker, true);
      window.removeEventListener("resize", positionCategoryPicker);
      window.removeEventListener("scroll", positionCategoryPicker, true);
    };
  }, [isCategoryPickerOpen]);

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function updateMarketingBinding(index, changes) {
    setForm((current) => ({
      ...current,
      marketingBindings: (current.marketingBindings || []).map((binding, bindingIndex) => (
        bindingIndex === index ? { ...binding, ...changes } : binding
      )),
    }));
  }

  function addMarketingBinding() {
    setForm((current) => ({
      ...current,
      marketingBindings: [...(current.marketingBindings || []), {
        ...createMarketingBinding("google_ads"),
        isPrimary: !(current.marketingBindings || []).some((binding) => binding.provider === "google_ads"),
      }],
    }));
  }

  function removeMarketingBinding(index) {
    setForm((current) => ({
      ...current,
      marketingBindings: (current.marketingBindings || []).filter((_, bindingIndex) => bindingIndex !== index),
    }));
  }

  async function loadGoogleStructure() {
    setGoogleStructure((current) => ({ ...current, status: "loading", message: "" }));
    try {
      const response = await fetch(apiUrl(`${apiPrefix}/marketing/google/structure`), {
        headers: getSessionHeaders(),
      });
      const data = await response.json();
      if (!data.success) {
        setGoogleStructure({ status: "error", campaigns: [], adGroups: [], keywords: [], message: data.message || "No se pudo leer Google Ads." });
        return;
      }
      setGoogleStructure({
        status: "ready",
        campaigns: data.campaigns || [],
        adGroups: data.adGroups || [],
        keywords: data.keywords || [],
        message: "",
      });
    } catch {
      setGoogleStructure({ status: "error", campaigns: [], adGroups: [], keywords: [], message: "No se pudo conectar con Google Ads." });
    }
  }

  function applyGoogleAdGroup(adGroup) {
    const campaign = googleStructure.campaigns.find((item) => String(item.id) === String(adGroup.campaignId));
    const keywords = googleStructure.keywords.filter((item) => String(item.adGroupId) === String(adGroup.id));
    const changes = {
      provider: "google_ads",
      providerLabel: "Google Ads",
      campaignExternalId: String(adGroup.campaignId || ""),
      campaignName: adGroup.campaignName || campaign?.name || "",
      campaignStatus: String(campaign?.status || "unknown").toLowerCase(),
      intentExternalId: String(adGroup.id || ""),
      intentKind: "ad_group",
      intentName: adGroup.name || "",
      intentTerms: keywords.map((item) => item.text).filter(Boolean),
    };
    setForm((current) => {
      const bindings = [...(current.marketingBindings || [])];
      const bindingIndex = bindings.findIndex((binding) => binding.provider === "google_ads");
      if (bindingIndex < 0) bindings.push({ ...createMarketingBinding("google_ads"), ...changes });
      else bindings[bindingIndex] = { ...bindings[bindingIndex], ...changes };
      return { ...current, marketingBindings: bindings };
    });
  }

  function updateBlock(blockId, changes) {
    if (editingBlock?.id === blockId) {
      setEditingBlock((current) => current ? { ...current, ...changes } : current);
      return;
    }
    setForm((current) => ({
      ...current,
      blocks: (current.blocks || []).map((block) => block.id === blockId ? { ...block, ...changes } : block),
    }));
  }

  function addBlock() {
    setMessage("");
    setIsNewBlockTypeOpen(false);
    setIsCategoryPickerOpen(false);
    setCategorySearch("");
    setEditingBlock(createBlock(newBlockType, storefrontName));
    setEditingBlockIndex(-1);
  }

  function editBlock(block, index) {
    setMessage("");
    setIsNewBlockTypeOpen(false);
    setEditingBlock(JSON.parse(JSON.stringify(block)));
    setEditingBlockIndex(index);
    setCategorySearch("");
  }

  function closeBlockEditor() {
    if (uploadingBlockId) return;
    setEditingBlock(null);
    setEditingBlockIndex(-1);
    setIsCategoryPickerOpen(false);
    setCategorySearch("");
  }

  function saveBlock(event) {
    event.preventDefault();
    if (!editingBlock || uploadingBlockId) return;
    setForm((current) => {
      const blocks = [...(current.blocks || [])];
      if (editingBlockIndex < 0) blocks.push(editingBlock);
      else blocks[editingBlockIndex] = editingBlock;
      return { ...current, blocks };
    });
    setEditingBlock(null);
    setEditingBlockIndex(-1);
    setIsCategoryPickerOpen(false);
    setCategorySearch("");
  }

  function removeBlock(blockId) {
    setForm((current) => ({ ...current, blocks: (current.blocks || []).filter((block) => block.id !== blockId) }));
  }

  function requestRemoveBlock(block, index) {
    const typeLabel = blockTypeOptions.find((option) => option.value === block.type)?.label || "seccion";
    setConfirmDialog({
      title: "Eliminar seccion",
      message: `Eliminar la seccion ${index + 1} (${typeLabel})?`,
      confirmLabel: "Eliminar",
      onConfirm: () => {
        removeBlock(block.id);
        setConfirmDialog(null);
      },
    });
  }

  function moveBlock(index, direction) {
    setForm((current) => {
      const blocks = [...(current.blocks || [])];
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= blocks.length) return current;
      [blocks[index], blocks[nextIndex]] = [blocks[nextIndex], blocks[index]];
      return { ...current, blocks };
    });
  }

  function toggleBlockCategory(block, category) {
    const selected = new Set(block.categories || []);
    if (selected.has(category)) selected.delete(category);
    else selected.add(category);
    updateBlock(block.id, { categories: [...selected] });
  }

  function toggleBlockProduct(block, productId) {
    const normalizedId = Number(productId);
    const selected = new Set((block.productIds || []).map(Number));
    if (selected.has(normalizedId)) selected.delete(normalizedId);
    else selected.add(normalizedId);
    updateBlock(block.id, { productIds: [...selected] });
  }

  function updateBlockLink(block, index, field, value) {
    const links = [...(block.links || [])];
    links[index] = { ...links[index], [field]: value };
    updateBlock(block.id, { links });
  }

  function getBlockSummary(block) {
    if (block.type === "product_grid") {
      if (block.selectionMode === "manual") {
        const count = (block.productIds || []).length;
        return `${count} producto${count === 1 ? "" : "s"} seleccionado${count === 1 ? "" : "s"}`;
      }
      const selectedCategories = block.categories || [];
      return selectedCategories.length ? selectedCategories.join(", ") : "Todas las categorias";
    }
    if (block.type === "image") return block.alt || block.caption || (block.imageUrl ? "Imagen configurada" : "Sin imagen");
    if (block.type === "button") return [block.label, block.path].filter(Boolean).join(" · ") || "Boton sin configurar";
    if (block.type === "links") return `${(block.links || []).length} link${(block.links || []).length === 1 ? "" : "s"}`;
    return block.title || block.body || "Sin contenido";
  }

  function openModal(landing = null) {
    setForm(landing
      ? {
        ...defaultLanding,
        ...landing,
        blocks: Array.isArray(landing.blocks) ? landing.blocks : [],
        marketingBindings: Array.isArray(landing.marketingBindings) ? landing.marketingBindings : [],
      }
      : { ...defaultLanding, blocks: [createBlock("hero", storefrontName), createBlock("product_grid", storefrontName)] });
    setMessage("");
    setEditingBlock(null);
    setEditingBlockIndex(-1);
    setIsCategoryPickerOpen(false);
    setCategorySearch("");
    setGoogleStructure({ status: "idle", campaigns: [], adGroups: [], keywords: [], message: "" });
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setForm(defaultLanding);
    setUploadingBlockId("");
    setIsNewBlockTypeOpen(false);
    setIsCategoryPickerOpen(false);
    setCategorySearch("");
    setEditingBlock(null);
    setEditingBlockIndex(-1);
  }

  function readFileAsDataUrl(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  async function uploadImage(file) {
    const dataUrl = await readFileAsDataUrl(file);
    const response = await fetch(apiUrl(`${apiPrefix}/product-images`), {
      method: "POST",
      headers: getSessionHeaders(),
      body: JSON.stringify({
        dataUrl,
        mimeType: file.type,
      }),
    });
    const data = await response.json();
    if (!data.success || !data.imageUrl) {
      throw new Error(data.message || "No se pudo subir la imagen.");
    }
    return data.imageUrl;
  }

  async function handleBlockImageFile(event, blockId) {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadingBlockId(blockId);
    setMessage("");
    try {
      updateBlock(blockId, { imageUrl: await uploadImage(file) });
    } catch (err) {
      setMessage(err.message || "No se pudo subir la imagen.");
    } finally {
      setUploadingBlockId("");
      event.target.value = "";
    }
  }

  async function saveLanding(event) {
    event.preventDefault();
    setMessage("");
    if (uploadingBlockId) {
      setMessage("Espera a que termine de subir la imagen.");
      return;
    }
    const hero = (form.blocks || []).find((block) => block.type === "hero");
    const heroTitle = String(hero?.title || "").trim();
    const heroBody = String(hero?.body || "").trim();
    if (!heroTitle || !heroBody) {
      setMessage("Agrega un titulo y texto al Hero antes de guardar la landing.");
      return;
    }
    const payload = {
      ...form,
      title: heroTitle,
      subtitle: heroBody,
      slug: normalizeSlug(form.slug || heroTitle),
    };
    const method = payload.id ? "PUT" : "POST";
    const path = payload.id ? `${apiPrefix}/landings/${payload.id}` : `${apiPrefix}/landings`;

    try {
      const response = await fetch(apiUrl(path), {
        method,
        headers: getSessionHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!data.success) {
        setMessage(data.message || "No se pudo guardar la landing.");
        return;
      }
      closeModal();
      setMessage("Landing guardada.");
      await loadLandings();
    } catch {
      setMessage("No se pudo conectar con la API.");
    }
  }

  async function toggleLanding(landing) {
    setLandings((current) => current.map((entry) => (
      entry.id === landing.id ? { ...entry, isActive: !landing.isActive } : entry
    )));
    try {
      const response = await fetch(apiUrl(`${apiPrefix}/landings/${landing.id}/active`), {
        method: "PATCH",
        headers: getSessionHeaders(),
        body: JSON.stringify({ isActive: !landing.isActive }),
      });
      const data = await response.json();
      if (!data.success) await loadLandings();
    } catch {
      await loadLandings();
    }
  }

  function deleteLanding(landing) {
    setConfirmDialog({
      title: "Eliminar landing",
      message: `Eliminar la landing "${landing.slug}"?`,
      confirmLabel: "Eliminar",
      onConfirm: () => confirmDeleteLanding(landing),
    });
  }

  async function confirmDeleteLanding(landing) {
    setConfirmDialog(null);
    try {
      const response = await fetch(apiUrl(`${apiPrefix}/landings/${landing.id}`), {
        method: "DELETE",
        headers: getSessionHeaders(),
      });
      const data = await response.json();
      if (!data.success) {
        setMessage(data.message || "No se pudo eliminar la landing.");
        return;
      }
      setMessage("Landing eliminada.");
      await loadLandings();
    } catch {
      setMessage("No se pudo conectar con la API.");
    }
  }

  function renderBlockEditor(block) {
    const typeLabel = blockTypeOptions.find((option) => option.value === block.type)?.label || block.type;
    const selectedProductIds = new Set((block.productIds || []).map(Number));
    const selectedCategories = block.categories || [];
    const normalizedSearch = categorySearch.trim().toLocaleLowerCase();
    const filteredCategories = categories.filter((category) => category.toLocaleLowerCase().includes(normalizedSearch));
    return (
      <article className="tablee-landing-block tablee-landing-block--modal" key={block.id}>
        <header>
          <div>
            <span>{editingBlockIndex < 0 ? "Nueva seccion" : `Seccion ${editingBlockIndex + 1}`}</span>
            <strong>{typeLabel}</strong>
          </div>
        </header>

        {["hero", "text", "product_grid"].includes(block.type) ? (
          <div className="tablee-carousel__form-grid tablee-landing-block__grid">
            <label className="tablee-carousel__field"><span>Etiqueta</span><input value={block.eyebrow || ""} onChange={(event) => updateBlock(block.id, { eyebrow: event.target.value })} /></label>
            <label className="tablee-carousel__field"><span>Titulo</span><input value={block.title || ""} onChange={(event) => updateBlock(block.id, { title: event.target.value })} /></label>
            <label className="tablee-carousel__field tablee-carousel__wide-field"><span>Texto</span><textarea rows="3" value={block.body || ""} onChange={(event) => updateBlock(block.id, { body: event.target.value })} /></label>
          </div>
        ) : null}

        {["hero", "image"].includes(block.type) ? (
          <div className="tablee-landing-block__image">
            {block.imageUrl ? <img src={block.imageUrl} alt="" /> : <span className="material-symbols-outlined">image</span>}
            <label>
              <input type="file" accept="image/*" onChange={(event) => handleBlockImageFile(event, block.id)} />
              {uploadingBlockId === block.id ? "Cargando..." : "Subir imagen"}
            </label>
            <button type="button" disabled={!block.imageUrl} onClick={() => updateBlock(block.id, { imageUrl: "" })}>Quitar</button>
          </div>
        ) : null}

        {block.type === "hero" ? (
          <div className="tablee-carousel__form-grid tablee-landing-block__grid">
            <label className="tablee-carousel__field"><span>Boton principal</span><input value={block.primaryLabel || ""} onChange={(event) => updateBlock(block.id, { primaryLabel: event.target.value })} /></label>
            <label className="tablee-carousel__field"><span>Ruta principal</span><input placeholder="/catalogo o #productos" value={block.primaryPath || ""} onChange={(event) => updateBlock(block.id, { primaryPath: event.target.value })} /></label>
            <label className="tablee-carousel__field"><span>Boton secundario</span><input value={block.secondaryLabel || ""} onChange={(event) => updateBlock(block.id, { secondaryLabel: event.target.value })} /></label>
            <label className="tablee-carousel__field"><span>Ruta secundaria</span><input value={block.secondaryPath || ""} onChange={(event) => updateBlock(block.id, { secondaryPath: event.target.value })} /></label>
          </div>
        ) : null}

        {block.type === "image" ? (
          <div className="tablee-carousel__form-grid tablee-landing-block__grid">
            <label className="tablee-carousel__field"><span>Texto alternativo</span><input value={block.alt || ""} onChange={(event) => updateBlock(block.id, { alt: event.target.value })} /></label>
            <label className="tablee-carousel__field"><span>Link opcional</span><input value={block.path || ""} onChange={(event) => updateBlock(block.id, { path: event.target.value })} /></label>
            <label className="tablee-carousel__field tablee-carousel__wide-field"><span>Pie de imagen</span><input value={block.caption || ""} onChange={(event) => updateBlock(block.id, { caption: event.target.value })} /></label>
          </div>
        ) : null}

        {block.type === "button" ? (
          <div className="tablee-carousel__form-grid tablee-landing-block__grid">
            <label className="tablee-carousel__field"><span>Texto</span><input value={block.label || ""} onChange={(event) => updateBlock(block.id, { label: event.target.value })} /></label>
            <label className="tablee-carousel__field"><span>Ruta</span><input value={block.path || ""} onChange={(event) => updateBlock(block.id, { path: event.target.value })} /></label>
            <label className="tablee-carousel__field"><span>Estilo</span><select value={block.style || "primary"} onChange={(event) => updateBlock(block.id, { style: event.target.value })}><option value="primary">Principal</option><option value="secondary">Secundario</option></select></label>
          </div>
        ) : null}

        {block.type === "product_grid" ? (
          <div className="tablee-landing-products-config">
            <div className="tablee-carousel__form-grid tablee-landing-block__grid">
              <label className="tablee-carousel__field"><span>Seleccion</span><select value={block.selectionMode || "filter"} onChange={(event) => updateBlock(block.id, { selectionMode: event.target.value, sort: event.target.value === "manual" ? "manual" : (block.sort === "manual" ? "featured" : block.sort) })}><option value="filter">Filtrar por categorias</option><option value="manual">Elegir productos</option></select></label>
              <label className="tablee-carousel__field"><span>Orden</span><select value={block.sort || "featured"} onChange={(event) => updateBlock(block.id, { sort: event.target.value })}><option value="featured">Destacados</option><option value="name">Nombre</option><option value="price_asc">Precio menor a mayor</option><option value="price_desc">Precio mayor a menor</option><option value="manual">Orden manual</option></select></label>
              <label className="tablee-carousel__field"><span>Maximo de productos</span><input type="number" min="1" max="48" value={block.limit || 12} onChange={(event) => updateBlock(block.id, { limit: Number(event.target.value) })} /></label>
            </div>
            {block.selectionMode !== "manual" ? (
              <div className="tablee-landing-category-picker" ref={categoryPickerRef}>
                <span className="tablee-landing-category-picker__label">Categorías</span>
                <button
                  type="button"
                  className="tablee-landing-category-picker__trigger"
                  aria-expanded={isCategoryPickerOpen}
                  onClick={() => setIsCategoryPickerOpen((current) => !current)}
                >
                  <span>{selectedCategories.length ? `${selectedCategories.length} categoría${selectedCategories.length === 1 ? "" : "s"} seleccionada${selectedCategories.length === 1 ? "" : "s"}` : "Todas las categorías"}</span>
                  <span className="material-symbols-outlined">expand_more</span>
                </button>
                {selectedCategories.length ? (
                  <div className="tablee-landing-category-picker__selected">
                    {selectedCategories.map((category) => (
                      <button type="button" key={category} onClick={() => toggleBlockCategory(block, category)}>
                        {category}<span className="material-symbols-outlined">close</span>
                      </button>
                    ))}
                  </div>
                ) : null}
                {isCategoryPickerOpen && categoryPickerPosition ? createPortal(
                  <div
                    ref={categoryPickerMenuRef}
                    className="tablee-landing-category-picker__menu"
                    style={categoryPickerPosition}
                  >
                    <label className="tablee-landing-category-picker__search">
                      <span className="material-symbols-outlined">search</span>
                      <input autoFocus value={categorySearch} onChange={(event) => setCategorySearch(event.target.value)} placeholder="Buscar categorías" />
                    </label>
                    <div className="tablee-landing-category-picker__options">
                      {filteredCategories.length ? filteredCategories.map((category) => {
                        const isSelected = selectedCategories.includes(category);
                        return (
                          <button
                            type="button"
                            key={category}
                            className={isSelected ? "is-selected" : ""}
                            onClick={() => toggleBlockCategory(block, category)}
                          >
                            <span>{category}</span>
                            <span className="material-symbols-outlined">{isSelected ? "check" : "add"}</span>
                          </button>
                        );
                      }) : <p>No hay categorías que coincidan.</p>}
                    </div>
                  </div>,
                  document.body
                ) : null}
              </div>
            ) : (
              <fieldset><legend>Productos</legend><div className="tablee-landing-choice-grid tablee-landing-choice-grid--products">{products.map((product) => <label key={product.id}><input type="checkbox" checked={selectedProductIds.has(Number(product.id))} onChange={() => toggleBlockProduct(block, product.id)} />{product.imageUrl ? <img src={product.imageUrl} alt="" /> : null}<span>{product.name}</span></label>)}</div></fieldset>
            )}
            <div className="tablee-landing-switches">
              <label className="tablee-landing-toggle"><input type="checkbox" checked={block.inStockOnly === true} onChange={(event) => updateBlock(block.id, { inStockOnly: event.target.checked })} /><span aria-hidden="true" /><strong>Solo con stock</strong></label>
              <label className="tablee-landing-toggle"><input type="checkbox" checked={block.showCategory !== false} onChange={(event) => updateBlock(block.id, { showCategory: event.target.checked })} /><span aria-hidden="true" /><strong>Mostrar categoria</strong></label>
              <label className="tablee-landing-toggle"><input type="checkbox" checked={block.showDescription === true} onChange={(event) => updateBlock(block.id, { showDescription: event.target.checked })} /><span aria-hidden="true" /><strong>Mostrar descripcion</strong></label>
              <label className="tablee-landing-toggle"><input type="checkbox" checked={block.showAddToCart !== false} onChange={(event) => updateBlock(block.id, { showAddToCart: event.target.checked })} /><span aria-hidden="true" /><strong>Mostrar agregar</strong></label>
            </div>
          </div>
        ) : null}

        {block.type === "links" ? (
          <div className="tablee-landing-block__links">
            <label className="tablee-carousel__field"><span>Titulo</span><input value={block.title || ""} onChange={(event) => updateBlock(block.id, { title: event.target.value })} /></label>
            {(block.links || []).map((link, linkIndex) => <div key={`${block.id}-${linkIndex}`}><input placeholder="Texto" value={link.label || ""} onChange={(event) => updateBlockLink(block, linkIndex, "label", event.target.value)} /><input placeholder="/ruta" value={link.path || ""} onChange={(event) => updateBlockLink(block, linkIndex, "path", event.target.value)} /><button type="button" onClick={() => updateBlock(block.id, { links: block.links.filter((_, indexValue) => indexValue !== linkIndex) })}><span className="material-symbols-outlined">close</span></button></div>)}
            <button type="button" onClick={() => updateBlock(block.id, { links: [...(block.links || []), { label: "", path: "" }] })}>Agregar link</button>
          </div>
        ) : null}

        {["hero", "text", "image", "button", "links"].includes(block.type) ? (
          <label className="tablee-carousel__field tablee-landing-block__align"><span>Alineacion</span><select value={block.align || "left"} onChange={(event) => updateBlock(block.id, { align: event.target.value })}><option value="left">Izquierda</option><option value="center">Centrada</option></select></label>
        ) : null}
      </article>
    );
  }

  return (
    <div className="tablee-carousel tablee-landings-admin">
      <header className="tablee-carousel__header">
        <img src={storeLogo} alt={storefrontName} />
        <div>
          <span>{storefrontName}</span>
          <h1>Constructor de landings</h1>
          <p>Crea paginas de campaña con contenido, imagenes, botones y productos vivos del catalogo.</p>
        </div>
        <div className="tablee-landings-admin__header-actions">
          <button type="button" className="tablee-carousel__secondary" onClick={() => openModal(createGoogleTablesPilot(storefrontName, defaultLanding, apiPrefix === "/tablee" ? "24030847091" : ""))}>
            <span className="material-symbols-outlined">ads_click</span>
            Borrador Google
          </button>
          <button type="button" onClick={() => openModal()}>Agregar landing</button>
        </div>
      </header>

      <section className="tablee-carousel__display tablee-home-admin__slides tablee-landings-admin__slides">
        <div className="tablee-carousel__display-head">
          <div>
            <span>Contenido</span>
            <h2>Landings publicadas</h2>
          </div>
          <strong>{landings.length} landings</strong>
        </div>

        <div className="tablee-home-admin__slides-table tablee-landings-admin__slides-table" aria-hidden="true">
          <span>Landing</span>
          <span>Slug</span>
          <span>Estado</span>
          <span>Acciones</span>
        </div>
        <div className="tablee-home-admin__slides-list tablee-landings-admin__slides-list">
          {landings.length ? landings.map((landing) => (
            <article key={landing.id} className={!landing.isActive ? "is-hidden" : ""}>
              <div className="tablee-home-admin__section-cell">
                <div>
                  <strong>{landing.title}</strong>
                  <small>{landing.subtitle || "Sin bajada configurada."}</small>
                  {(landing.marketingBindings || []).length ? (
                    <small className="tablee-landings-admin__campaign-summary">
                      {(landing.marketingBindings || []).map((binding) => binding.providerLabel).join(" · ")}
                    </small>
                  ) : null}
                </div>
              </div>
              <a href={getPublicLandingUrl(landing, publicBaseUrl)} target="_blank" rel="noreferrer">
                {Array.isArray(landing.blocks) && landing.blocks.length ? `/campanas/${landing.slug}/` : `/${landing.slug}/`}
              </a>
              <label className="tablee-carousel__switch">
                <input type="checkbox" checked={landing.isActive} onChange={() => toggleLanding(landing)} />
                <span />
                <strong>{landing.isActive ? "Activo" : "Oculto"}</strong>
              </label>
              <div className="tablee-home-admin__row-actions">
                <button type="button" onClick={() => openModal(landing)} aria-label={`Editar ${landing.slug}`}>
                  <span className="material-symbols-outlined">edit</span>
                </button>
                <button type="button" className="tablee-carousel__danger" onClick={() => deleteLanding(landing)} aria-label={`Eliminar ${landing.slug}`}>
                  <span className="material-symbols-outlined">delete</span>
                </button>
              </div>
            </article>
          )) : (
            <p className="tablee-carousel__empty">No hay landings configuradas.</p>
          )}
        </div>
        {message ? <p className="tablee-landings-admin__message">{message}</p> : null}
      </section>

      {showModal ? (
        <div className="tablee-carousel__modal-backdrop" role="presentation" onMouseDown={closeModal}>
          <form className="tablee-carousel__modal tablee-landings-admin__modal" onSubmit={saveLanding} onMouseDown={(event) => event.stopPropagation()}>
            <div className="tablee-carousel__modal-head">
              <div>
                <span>{storefrontName}</span>
                <h2>{form.id ? "Editar landing" : "Agregar landing"}</h2>
              </div>
              <button type="button" className="tablee-carousel__icon-button" onClick={closeModal} aria-label="Cerrar">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="tablee-landing-builder-panel">
                <section className="tablee-landing-marketing-panel">
                  <header>
                    <div>
                      <span>Alineación de campaña</span>
                      <h3>Destino e intención</h3>
                      <p>Conecta esta página con la campaña, grupo de anuncios o audiencia que enviará tráfico.</p>
                    </div>
                    <div>
                      <button type="button" className="tablee-carousel__secondary" onClick={loadGoogleStructure} disabled={googleStructure.status === "loading"}>
                        <span className="material-symbols-outlined">sync</span>
                        {googleStructure.status === "loading" ? "Leyendo Google..." : "Leer Google Ads"}
                      </button>
                      <button type="button" onClick={addMarketingBinding}>
                        <span className="material-symbols-outlined">add</span>
                        Agregar destino
                      </button>
                    </div>
                  </header>

                  {googleStructure.message ? <p className="tablee-landing-marketing-panel__message">{googleStructure.message}</p> : null}

                  {googleStructure.status === "ready" && googleStructure.adGroups.length ? (
                    <div className="tablee-landing-google-structure">
                      <strong>Grupos disponibles en Google Ads</strong>
                      <div>
                        {googleStructure.adGroups.map((adGroup) => (
                          <button
                            type="button"
                            key={`${adGroup.campaignId}-${adGroup.id}`}
                            onClick={() => applyGoogleAdGroup(adGroup)}
                          >
                            <span>{adGroup.campaignName}</span>
                            <strong>{adGroup.name}</strong>
                            <small>{googleStructure.keywords.filter((keyword) => String(keyword.adGroupId) === String(adGroup.id)).length} palabras clave</small>
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {(form.marketingBindings || []).length ? (
                    <div className="tablee-landing-marketing-bindings">
                      {form.marketingBindings.map((binding, bindingIndex) => (
                        <article key={`${binding.provider}-${bindingIndex}`}>
                          <div className="tablee-landing-marketing-bindings__head">
                            <div className="tablee-landing-marketing-segment" role="group" aria-label={`Origen del destino ${bindingIndex + 1}`}>
                              {[
                                ["google_ads", "Google Ads"],
                                ["meta_ads", "Meta Ads"],
                                ["manual", "Manual"],
                              ].map(([provider, label]) => (
                                <button
                                  type="button"
                                  key={provider}
                                  className={binding.provider === provider ? "is-active" : ""}
                                  onClick={() => updateMarketingBinding(bindingIndex, {
                                    provider,
                                    providerLabel: label,
                                    finalUrlSuffix: provider === "google_ads" && !binding.finalUrlSuffix ? GOOGLE_VALUE_TRACK_SUFFIX : binding.finalUrlSuffix,
                                  })}
                                >
                                  {label}
                                </button>
                              ))}
                            </div>
                            <button type="button" className="tablee-carousel__danger" onClick={() => removeMarketingBinding(bindingIndex)} aria-label={`Eliminar destino ${bindingIndex + 1}`}>
                              <span className="material-symbols-outlined">delete</span>
                            </button>
                          </div>

                          <div className="tablee-landing-marketing-grid">
                            <label><span>Nombre campaña</span><input value={binding.campaignName || ""} onChange={(event) => updateMarketingBinding(bindingIndex, { campaignName: event.target.value })} placeholder={`Ej. ${storefrontName} Search`} /></label>
                            <label><span>ID campaña</span><input value={binding.campaignExternalId || ""} onChange={(event) => updateMarketingBinding(bindingIndex, { campaignExternalId: event.target.value })} placeholder="24030847091" /></label>
                            <label><span>Grupo / intención</span><input value={binding.intentName || ""} onChange={(event) => updateMarketingBinding(bindingIndex, { intentName: event.target.value })} placeholder="Ej. Tabla de quesos" /></label>
                            <label><span>ID grupo</span><input value={binding.intentExternalId || ""} onChange={(event) => updateMarketingBinding(bindingIndex, { intentExternalId: event.target.value })} placeholder="Opcional" /></label>
                            <label className="is-wide"><span>Palabras clave o señales</span><input value={(binding.intentTerms || []).join(", ")} onChange={(event) => updateMarketingBinding(bindingIndex, { intentTerms: event.target.value.split(",").map((value) => value.trim()).filter(Boolean) })} placeholder="tabla de quesos, tablas para compartir" /></label>
                            <label className="is-wide"><span>Final URL suffix / ValueTrack</span><textarea value={binding.finalUrlSuffix || ""} onChange={(event) => updateMarketingBinding(bindingIndex, { finalUrlSuffix: event.target.value })} placeholder="utm_source=google&utm_medium=cpc..." /></label>
                            <label className="is-wide"><span>Notas internas</span><textarea value={binding.notes || ""} onChange={(event) => updateMarketingBinding(bindingIndex, { notes: event.target.value })} placeholder="Objetivo, hipótesis y criterio de validación." /></label>
                          </div>

                          <div className="tablee-landing-marketing-status">
                            <span>Estado</span>
                            {[
                              ["draft", "Borrador"],
                              ["ready", "Listo"],
                              ["live", "En uso"],
                              ["paused", "Pausado"],
                            ].map(([status, label]) => (
                              <button type="button" key={status} className={binding.destinationStatus === status ? "is-active" : ""} onClick={() => updateMarketingBinding(bindingIndex, { destinationStatus: status })}>{label}</button>
                            ))}
                          </div>
                        </article>
                      ))}
                    </div>
                  ) : (
                    <p className="tablee-carousel__empty">Sin destino de marketing. La landing puede usarse de forma orgánica.</p>
                  )}
                </section>

                <div className="tablee-landing-builder">
                  <div className="tablee-landing-builder__toolbar">
                    <div><span>Contenido dinamico</span><strong>{(form.blocks || []).length} secciones</strong></div>
                    <div ref={sectionTypeDropdownRef} className={`tablee-landing-section-picker ${isNewBlockTypeOpen ? "is-open" : ""}`}>
                      <button
                        type="button"
                        className="tablee-landing-section-picker__trigger"
                        onClick={() => setIsNewBlockTypeOpen((current) => !current)}
                        aria-haspopup="listbox"
                        aria-expanded={isNewBlockTypeOpen}
                      >
                        <span>{blockTypeOptions.find((option) => option.value === newBlockType)?.label || "Seleccionar seccion"}</span>
                        <span className="material-symbols-outlined">expand_more</span>
                      </button>
                      {isNewBlockTypeOpen ? (
                        <div className="tablee-landing-section-picker__menu" role="listbox" aria-label="Tipo de seccion">
                          {blockTypeOptions.map((option) => (
                            <button
                              type="button"
                              role="option"
                              aria-selected={option.value === newBlockType}
                              className={option.value === newBlockType ? "is-active" : ""}
                              key={option.value}
                              onClick={() => {
                                setNewBlockType(option.value);
                                setIsNewBlockTypeOpen(false);
                              }}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                    <button type="button" onClick={addBlock}><span className="material-symbols-outlined">add</span> Agregar seccion</button>
                  </div>
                  {(form.blocks || []).length ? (
                    <div className="tablee-landing-sections-table-wrap">
                      <table className="tablee-landing-sections-table" aria-label="Secciones de la landing">
                        <thead>
                          <tr>
                            <th>Orden</th>
                            <th>Seccion</th>
                            <th>Contenido</th>
                            <th>Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {form.blocks.map((block, index) => (
                            <tr key={block.id}>
                              <td data-label="Orden"><strong>{index + 1}</strong></td>
                              <td data-label="Seccion">
                                <span className="tablee-landing-sections-table__type">{blockTypeOptions.find((option) => option.value === block.type)?.label || block.type}</span>
                              </td>
                              <td data-label="Contenido"><span className="tablee-landing-sections-table__summary">{getBlockSummary(block)}</span></td>
                              <td data-label="Acciones">
                                <div className="tablee-landing-sections-table__actions">
                                  <button type="button" disabled={index === 0} onClick={() => moveBlock(index, -1)} aria-label={`Mover seccion ${index + 1} arriba`} title="Mover arriba"><span className="material-symbols-outlined">arrow_upward</span></button>
                                  <button type="button" disabled={index === form.blocks.length - 1} onClick={() => moveBlock(index, 1)} aria-label={`Mover seccion ${index + 1} abajo`} title="Mover abajo"><span className="material-symbols-outlined">arrow_downward</span></button>
                                  <button type="button" onClick={() => editBlock(block, index)} aria-label={`Editar seccion ${index + 1}`} title="Editar"><span className="material-symbols-outlined">edit</span></button>
                                  <button type="button" className="tablee-carousel__danger" onClick={() => requestRemoveBlock(block, index)} aria-label={`Eliminar seccion ${index + 1}`} title="Eliminar"><span className="material-symbols-outlined">delete</span></button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : <p className="tablee-carousel__empty">Agrega un Hero y una seccion de Productos para comenzar.</p>}
                </div>
            </div>

            <div className="tablee-carousel__modal-actions tablee-delivery__modal-actions">
              <div className="tablee-delivery__footer-left">
                <label className="tablee-home-admin__publish-toggle tablee-delivery__publish-toggle">
                  <span>Publicado</span>
                  <input type="checkbox" checked={form.isActive} onChange={(event) => updateField("isActive", event.target.checked)} />
                  <i aria-hidden="true" />
                </label>
                {message ? <p>{message}</p> : null}
              </div>
              <div className="tablee-delivery__footer-buttons">
                <button type="button" className="tablee-carousel__secondary" onClick={closeModal}>Cancelar</button>
                <button type="submit" disabled={Boolean(uploadingBlockId)}>{uploadingBlockId ? "Subiendo imagen..." : (form.id ? "Guardar cambios" : "Agregar landing")}</button>
              </div>
            </div>
          </form>
        </div>
      ) : null}

      {editingBlock ? (
        <div className="tablee-carousel__modal-backdrop tablee-landing-block-modal-backdrop" role="presentation" onMouseDown={closeBlockEditor}>
          <form className="tablee-carousel__modal tablee-landing-block-modal" onSubmit={saveBlock} onMouseDown={(event) => event.stopPropagation()}>
            <div className="tablee-carousel__modal-head">
              <div>
                <span>Constructor de landing</span>
                <h2>{editingBlockIndex < 0 ? "Agregar seccion" : "Editar seccion"}</h2>
              </div>
              <button type="button" className="tablee-carousel__icon-button" onClick={closeBlockEditor} disabled={Boolean(uploadingBlockId)} aria-label="Cerrar editor de seccion">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="tablee-landing-block-modal__content">
              {renderBlockEditor(editingBlock)}
            </div>
            <div className="tablee-carousel__modal-actions tablee-landing-block-modal__actions">
              <div className="tablee-carousel__footer-left">{message ? <p>{message}</p> : null}</div>
              <div className="tablee-carousel__footer-buttons">
                <button type="button" className="tablee-carousel__secondary" onClick={closeBlockEditor} disabled={Boolean(uploadingBlockId)}>Cancelar</button>
                <button type="submit" disabled={Boolean(uploadingBlockId)}>{uploadingBlockId ? "Subiendo imagen..." : (editingBlockIndex < 0 ? "Agregar seccion" : "Guardar seccion")}</button>
              </div>
            </div>
          </form>
        </div>
      ) : null}

      <ConfirmDialog
        open={Boolean(confirmDialog)}
        title={confirmDialog?.title}
        message={confirmDialog?.message}
        confirmLabel={confirmDialog?.confirmLabel}
        onConfirm={confirmDialog?.onConfirm}
        onCancel={() => setConfirmDialog(null)}
      />
    </div>
  );
}

export default TableeLandings;
